/**
 * Dev-only mock mode helpers.
 *
 * Mock mode is active ONLY when BOTH conditions hold:
 *   1. The app runs under the Vite dev server (`import.meta.env.DEV`).
 *   2. The page URL carries `?mock=1` (or `?mock=true`).
 *
 * Everything gated behind `isMockMode()` is tree-shaken out of production
 * builds, so this scaffolding can never ship to a real Genesys embed.
 *
 * The mock replaces the Genesys side of the integration (OAuth + the Genesys
 * notification WebSocket + the Conversations API). The backend "middleware"
 * WebSocket is left REAL so dealer / resume / activity-feed round-trip against a
 * locally running worker (default `localhost:5000`).
 */

export interface MockParams {
  conversationId: string;
  language: string;
  agentLanguage: string;
  agentName: string;
  clientId: string;
}

/** True only under the dev server with `?mock=1`. */
export function isMockMode(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('mock');
    return value === '1' || value === 'true';
  } catch {
    return false;
  }
}

/** Reads the conversation/agent identity for the mock session from the URL. */
export function getMockParams(): MockParams {
  const params = new URLSearchParams(window.location.search);
  return {
    conversationId: params.get('cId') || 'mock-conv-001',
    language: params.get('lang') || 'nl',
    agentLanguage: params.get('agentLang') || 'nl',
    agentName: params.get('agent') || 'Test Agent',
    clientId: import.meta.env.VITE_GENESYS_CLIENT_ID || 'mock-client-id',
  };
}

/**
 * Resolves the origin of the parent harness page so postMessage can target a
 * specific origin instead of the insecure `'*'` wildcard. The iframe's
 * `document.referrer` is the embedding harness URL; falls back to the app's own
 * origin when standalone or when the referrer is unavailable.
 */
export function getHarnessOrigin(): string {
  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // Malformed referrer — fall through to the app origin.
  }
  return window.location.origin;
}

/** Posts a message to the parent harness window (no-op when standalone). */
export function postToHarness(message: Record<string, unknown>): void {
  try {
    window.parent.postMessage(message, getHarnessOrigin());
  } catch {
    // No parent window — ignore.
  }
}
